import { useRef, useState } from "react";
import { MessageSquare, Send, X } from "lucide-react";
import { API } from "@/lib/api";
import { useLocation } from "react-router-dom";

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Welcome to EuropEdu. I can explain our services and approved prices, help you search jobs, sponsors and courses, and arrange a paid consultation. I cannot guarantee jobs, sponsorship, admission or visas.",
  }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useRef(null);
  const { pathname } = useLocation();

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": localStorage.getItem("europedu_tenant") || "europedu",
          ...(localStorage.getItem("europedu_token") ? { Authorization: `Bearer ${localStorage.getItem("europedu_token")}` } : {}),
        },
        body: JSON.stringify({ message: text, session_id: session.current, page_context: pathname }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const chunk = p.replace(/^data: ?/, "");
          if (chunk.startsWith("[[SESSION]]")) { session.current = chunk.replace("[[SESSION]]", ""); continue; }
          if (chunk === "[[DONE]]") continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "The assistant is unavailable at the moment. Please try again." }]);
    }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="chatbot-toggle" onClick={() => setOpen(!open)} aria-label="Support assistant"
        className="fixed bottom-6 right-6 z-50 btn btn-primary shadow-lg">
        {open ? <X className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask EuropEdu"}</span>
      </button>
      {open && (
        <div data-testid="chatbot-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[28rem] card flex flex-col overflow-hidden reveal">
          <div className="px-4 py-3 bg-[#1e3a8a] text-white">
            <p className="font-semibold text-sm">EuropEdu Support Assistant</p>
            <p className="text-[11px] text-blue-100">Grounded in approved EuropEdu content. Not immigration advice.</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : ""}>
                <span className={`inline-block text-sm px-3 py-2 rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "bg-[#1e3a8a] text-white" : "bg-slate-100 text-slate-800"}`}>
                  {m.content || "…"}
                </span>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-200 flex gap-2">
            <input data-testid="chatbot-input" className="field" value={input} placeholder="Ask a question"
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
            <button data-testid="chatbot-send" onClick={send} disabled={busy} className="btn btn-primary px-3" aria-label="Send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
